import * as React from "react"
import { FormikConfig, FormikErrors } from "formik"
import { Form, FormElement } from "@modules/form/components/Form"
import { MdArrowForward } from "react-icons/md"
import {
  Button,
  StyledModalActions,
  Notification,
  Text,
  InputConnectedField,
  SelectConnectedField,
} from "gatsby-interface"
import {
  netlifyIntegrationConfig as text,
  ui as uiText,
} from "@modules/locales/default.json"
import { useCreateNetlifyIntegration } from "../hooks/useCreateNetlifyIntegration"
import { FormattedMessage } from "@modules/locales"
import { useNetlifyAccountOptions } from "../hooks/useNetlifyAccountOptions"

type FormValues = {
  siteName: string
  netlifyAccount: string
}

const validateForm: FormikConfig<FormValues>["validate"] = values => {
  const errors: FormikErrors<FormValues> = {}

  if (!values.netlifyAccount) {
    errors.netlifyAccount = text.messages.accountValidateRequired
  }

  if (!values.siteName) {
    errors.siteName = text.messages.siteNameValidateRequired
  } else if (!values.siteName.match(/^[a-zA-Z0-9-]+$/)) {
    errors.siteName = text.messages.siteNameValidateFormat
  }

  return errors
}

export type NewNetlifyIntegrationFormProps = {
  siteId: string
  onComplete: Parameters<typeof useCreateNetlifyIntegration>[0]
  onSwitchToExisting: () => void
  onCancel: () => void
}

export function NewNetlifyIntegrationForm({
  siteId,
  onComplete,
  onSwitchToExisting,
  onCancel,
}: NewNetlifyIntegrationFormProps) {
  const [
    createNetlifyIntegration,
    { loading, errorAlert },
  ] = useCreateNetlifyIntegration(onComplete)
  const [
    netlifyAccountOptions,
    { loading: loadingAccounts },
  ] = useNetlifyAccountOptions(siteId)

  return (
    <Form<FormValues>
      initialValues={{
        siteName: "",
        netlifyAccount: "",
      }}
      validate={validateForm}
      onSubmit={values =>
        createNetlifyIntegration(siteId, values.siteName, values.netlifyAccount)
      }
    >
      {({ touched, errors, values, handleSubmit }) => {
        const invalidAccount =
          !values.netlifyAccount ||
          (touched.netlifyAccount && errors.netlifyAccount)

        const invalidSiteName =
          !values.siteName || (touched.siteName && errors.siteName)

        const disabled = Boolean(invalidAccount || invalidSiteName)

        return (
          <FormElement applySpacing onSubmit={handleSubmit}>
            <Text css={{ margin: 0 }}>{text.headers.connectToNew}</Text>

            <Notification
              variant="SECONDARY"
              tone="BRAND"
              content={
                <div>
                  <FormattedMessage<never, "button">
                    message={text.messages.connectToExistingSiteCallout}
                    tags={{
                      button: function(content) {
                        return (
                          <Button
                            onClick={onSwitchToExisting}
                            variant="GHOST"
                            size="M"
                            css={{ padding: 0, fontSize: `inherit` }}
                            aria-label={text.actions.switchToExistingSite}
                          >
                            {content}
                          </Button>
                        )
                      },
                    }}
                  />
                </div>
              }
            />

            <InputConnectedField
              label={text.labels.siteName}
              name="siteName"
              required
            />

            <SelectConnectedField
              name="netlifyAccount"
              label={text.labels.selectAccount}
              required
              options={netlifyAccountOptions}
              disabled={loadingAccounts}
            />

            {errorAlert}

            <StyledModalActions>
              <Button variant="SECONDARY" type={`reset`} onClick={onCancel}>
                {uiText.actions.cancel}
              </Button>

              <Button
                type="submit"
                loading={loading}
                disabled={disabled}
                rightIcon={<MdArrowForward />}
              >
                {text.actions.create}
              </Button>
            </StyledModalActions>
          </FormElement>
        )
      }}
    </Form>
  )
}
